import { cn } from '../../lib/cn'

interface EmptyPlateProps {
  title: string
  description: string
  headingLevel?: 2 | 3
  /** Optional CTA — omit where there is genuinely nothing for the member to do yet. */
  action?: React.ReactNode
  className?: string
}

/**
 * The club's empty state (components card §X): dashed brass frame, the pin and
 * flag, and a line in the club's voice.
 *
 * This carries real weight in this build — the clubhouse has its chrome but no
 * data behind it yet, so EmptyPlate is what a member actually sees on the
 * Grounds, the Register and the Lounge. It says the truth ("no rounds yet")
 * rather than dressing the page with invented scores.
 */
export function EmptyPlate({ title, description, headingLevel = 3, action, className }: EmptyPlateProps) {
  const Heading = headingLevel === 2 ? 'h2' : 'h3'

  return (
    <div
      className={cn(
        'rounded-club-lg border border-dashed border-brass/55 bg-parchment px-[30px] py-11 text-center',
        className,
      )}
    >
      <svg
        width="64"
        height="56"
        viewBox="0 0 70 62"
        fill="none"
        aria-hidden="true"
        className="mx-auto mb-4 block"
      >
        <path d="M6 52 Q35 45 64 52" stroke="var(--color-sage)" strokeWidth="1.5" />
        <line x1="26" y1="52" x2="26" y2="12" stroke="var(--color-pine)" strokeWidth="1.8" />
        <circle cx="26" cy="11" r="2.6" fill="var(--color-pine)" />
        <path d="M26 15 L48 21 L26 28" fill="var(--color-flag)" opacity="0.9" />
      </svg>
      <Heading className="mb-2 font-display text-[21px] font-medium leading-tight text-pine">{title}</Heading>
      <p className="mx-auto mb-[22px] max-w-[34ch] text-[13.5px] text-ink-soft">{description}</p>
      {action}
    </div>
  )
}
